import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
  IconButton,
} from '@mui/material';
import ArchiveIcon from '@mui/icons-material/Archive';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import MarkEmailUnreadIcon from '@mui/icons-material/MarkEmailUnread';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import UnarchiveIcon from '@mui/icons-material/Unarchive';
import AuthContext from '../contexts/AuthContext';
import { usePractice } from '../contexts/PracticeContext';

const ROWS_PER_PAGE_OPTIONS = [5, 10, 25];
const SUBJECT_PREVIEW_LENGTH = 120;
const REDIRECT_NOTICE_KEY = 'redirect_notice';

function formatMessageDate(value) {
  if (!value) return 'Unknown';
  const date = new Date(value);
  const now = new Date();
  const formatter = new Intl.DateTimeFormat('en-US', (
    date.getFullYear() === now.getFullYear()
      ? {
          month: 'short',
          day: 'numeric',
          hour: 'numeric',
          minute: '2-digit',
        }
      : {
          month: 'short',
          day: 'numeric',
          year: '2-digit',
          hour: 'numeric',
          minute: '2-digit',
        }
  ));
  return formatter.format(date);
}

function formatMessageTableDate(value) {
  if (!value) return 'Unknown';
  return new Intl.DateTimeFormat('en-US', {
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  }).format(new Date(value));
}

function dispatchMessagesChanged() {
  window.dispatchEvent(new Event('messagesChanged'));
}

function getMessageFromLabel(message) {
  return message.from_user_name || 'Auto';
}

function isMessageRead(message) {
  return Boolean(message?.is_read);
}

function getMessageIcon(message) {
  switch (message.type) {
    case 'person-to-person':
    default:
      return <MailOutlineIcon fontSize="small" />;
  }
}

function truncateMiddle(value, maxLength = SUBJECT_PREVIEW_LENGTH) {
  if (!value || value.length <= maxLength) return value;
  const available = maxLength - 1;
  const left = Math.ceil(available / 2);
  const right = Math.floor(available / 2);
  return `${value.slice(0, left)}…${value.slice(value.length - right)}`;
}

function renderInlineMarkdown(text) {
  if (!text) return text;

  const tokenPattern = /(\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)|\*\*([^*]+)\*\*|`([^`]+)`|\*([^*]+)\*)/g;
  const nodes = [];
  let lastIndex = 0;
  let match;

  while ((match = tokenPattern.exec(text)) !== null) {
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }

    if (match[2] && match[3]) {
      nodes.push(
        <a key={`${match.index}-link`} href={match[3]} target="_blank" rel="noreferrer">
          {match[2]}
        </a>
      );
    } else if (match[4]) {
      nodes.push(<strong key={`${match.index}-strong`}>{match[4]}</strong>);
    } else if (match[5]) {
      nodes.push(
        <Box
          key={`${match.index}-code`}
          component="code"
          sx={{
            display: 'inline-block',
            fontFamily: 'monospace',
            px: 0.5,
            py: 0.125,
            borderRadius: 0.5,
            bgcolor: 'action.hover',
          }}
        >
          {match[5]}
        </Box>
      );
    } else if (match[6]) {
      nodes.push(<em key={`${match.index}-em`}>{match[6]}</em>);
    }

    lastIndex = tokenPattern.lastIndex;
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }

  return nodes;
}

function renderMarkdownBlocks(markdown) {
  const lines = String(markdown || '').replace(/\r/g, '').split('\n');
  const blocks = [];
  let index = 0;
  let key = 0;

  while (index < lines.length) {
    const line = lines[index];
    const trimmed = line.trim();

    if (!trimmed) {
      index += 1;
      continue;
    }

    const headingMatch = trimmed.match(/^(#{1,6})\s+(.*)$/);
    if (headingMatch) {
      const level = headingMatch[1].length;
      const variantMap = {
        1: 'h5',
        2: 'h6',
        3: 'subtitle1',
        4: 'subtitle2',
        5: 'body1',
        6: 'body2',
      };
      blocks.push(
        <Typography
          key={`md-${key}`}
          variant={variantMap[level]}
          component={`h${Math.min(level, 6)}`}
          sx={{ mt: blocks.length === 0 ? 0 : 2, mb: 1 }}
        >
          {renderInlineMarkdown(headingMatch[2])}
        </Typography>
      );
      key += 1;
      index += 1;
      continue;
    }

    if (/^[-*]\s+/.test(trimmed)) {
      const items = [];
      while (index < lines.length && /^[-*]\s+/.test(lines[index].trim())) {
        items.push(lines[index].trim().replace(/^[-*]\s+/, ''));
        index += 1;
      }
      const listKey = key;
      blocks.push(
        <Box key={`md-${listKey}`} component="ul" sx={{ mt: 0, mb: 2, pl: 3 }}>
          {items.map((item, itemIndex) => (
            <Box key={`md-${listKey}-${itemIndex}`} component="li" sx={{ mb: 0.5 }}>
              <Typography variant="body1">{renderInlineMarkdown(item)}</Typography>
            </Box>
          ))}
        </Box>
      );
      key += 1;
      continue;
    }

    const paragraphLines = [];
    while (index < lines.length && lines[index].trim() && !/^(#{1,6})\s+/.test(lines[index].trim()) && !/^[-*]\s+/.test(lines[index].trim())) {
      paragraphLines.push(lines[index].trim());
      index += 1;
    }
    blocks.push(
      <Typography key={`md-${key}`} variant="body1" sx={{ mb: 2 }}>
        {renderInlineMarkdown(paragraphLines.join(' '))}
      </Typography>
    );
    key += 1;
  }

  return blocks;
}

function MessageStatusChip({ message }) {
  if (message.is_archived) {
    return <Chip label="Archived" size="small" variant="outlined" />;
  }
  return null;
}

export default function Messages() {
  const apiBase = process.env.REACT_APP_PORTAL_API_URL || '';
  const navigate = useNavigate();
  const { messageId } = useParams();
  const { tokens, authData, user } = useContext(AuthContext);
  const { selectedPractice, setSelectedPractice } = usePractice();

  const [messages, setMessages] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [archivedFilter, setArchivedFilter] = useState('active');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [dateSortDirection, setDateSortDirection] = useState('desc');

  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [actionError, setActionError] = useState('');
  const [actionTargetId, setActionTargetId] = useState(null);
  const [listRefreshNonce, setListRefreshNonce] = useState(0);
  const autoMarkedIdsRef = useRef(new Set());
  const [composeOpen, setComposeOpen] = useState(false);
  const [recipientOptions, setRecipientOptions] = useState([]);
  const [recipientLoading, setRecipientLoading] = useState(false);
  const [recipientSearchInput, setRecipientSearchInput] = useState('');
  const [selectedRecipient, setSelectedRecipient] = useState(null);
  const [composeSubject, setComposeSubject] = useState('');
  const [composeBody, setComposeBody] = useState('');
  const [composeAttachments, setComposeAttachments] = useState([]);
  const [composeError, setComposeError] = useState('');
  const [composeSubmitting, setComposeSubmitting] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSearchText(searchInput.trim());
      setPage(0);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchInput]);

  useEffect(() => {
    if (!tokens?.id_token || !selectedPractice?.id) {
      setMessages([]);
      setTotal(0);
      setLoading(false);
      setError('');
      return;
    }

    const controller = new AbortController();
    const loadMessages = async () => {
      setLoading(true);
      setError('');
      try {
        const params = new URLSearchParams({
          organization_id: selectedPractice.id,
          skip: String(page * rowsPerPage),
          limit: String(rowsPerPage),
          sort_by: 'created_at',
          sort_dir: dateSortDirection,
        });
        if (searchText) params.set('search_text', searchText);
        if (statusFilter !== 'all') params.set('status', statusFilter);
        if (archivedFilter !== 'active') params.set('include_archived', 'true');

        const response = await fetch(`${apiBase}/messages?${params.toString()}`, {
          headers: { Authorization: `Bearer ${tokens.id_token}` },
          signal: controller.signal,
        });
        if (!response.ok) {
          throw new Error('Failed to load messages');
        }

        const data = await response.json();
        const items = Array.isArray(data.items) ? data.items : [];
        const visibleItems = archivedFilter === 'all'
          ? items
          : items.filter((item) => archivedFilter === 'archived' ? item.is_archived : !item.is_archived);
        setMessages(visibleItems);
        setTotal(Number.isFinite(data.total) ? data.total : visibleItems.length);
      } catch (err) {
        if (controller.signal.aborted) return;
        setMessages([]);
        setTotal(0);
        setError(err.message || 'Failed to load messages');
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    };

    loadMessages();
    return () => controller.abort();
  }, [apiBase, archivedFilter, dateSortDirection, listRefreshNonce, page, rowsPerPage, searchText, selectedPractice?.id, statusFilter, tokens?.id_token]);

  useEffect(() => {
    if (!tokens?.id_token || !messageId) {
      setDetail(null);
      setDetailLoading(false);
      setDetailError('');
      return;
    }

    const controller = new AbortController();
    const loadMessage = async () => {
      setDetailLoading(true);
      setDetailError('');
      try {
        const response = await fetch(`${apiBase}/messages/${messageId}`, {
          headers: { Authorization: `Bearer ${tokens.id_token}` },
          signal: controller.signal,
        });
        if (!response.ok) {
          if (response.status === 404) {
            sessionStorage.setItem(
              REDIRECT_NOTICE_KEY,
              'You were redirected to your home page because that message is not available for your account.'
            );
            navigate('/', { replace: true });
            return;
          }
          throw new Error(response.status === 404 ? 'Message not found' : 'Failed to load message');
        }

        const data = await response.json();
        if (controller.signal.aborted) return;

        setDetail(data);

        if (String(selectedPractice?.id || '') !== String(data.organization_id || '')) {
          const matchingOrganization = authData?.organizations?.find(
            (organization) => String(organization.id) === String(data.organization_id)
          );
          if (matchingOrganization) {
            setSelectedPractice(matchingOrganization);
          }
        }

        if (
          String(data.to_user_id) === String(user?.id) &&
          !data.is_read &&
          !autoMarkedIdsRef.current.has(String(data.id))
        ) {
          autoMarkedIdsRef.current.add(String(data.id));
          const markReadResponse = await fetch(`${apiBase}/messages/${data.id}/toggle-read`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${tokens.id_token}` },
            signal: controller.signal,
          });
          if (markReadResponse.ok) {
            const updated = await markReadResponse.json();
            if (!controller.signal.aborted) {
              setDetail(updated);
              setMessages((current) => current.map((item) => (
                String(item.id) === String(updated.id) ? updated : item
              )));
              dispatchMessagesChanged();
            }
          }
        }
      } catch (err) {
        if (controller.signal.aborted) return;
        setDetail(null);
        setDetailError(err.message || 'Failed to load message');
      } finally {
        if (!controller.signal.aborted) {
          setDetailLoading(false);
        }
      }
    };

    loadMessage();
    return () => controller.abort();
  }, [
    apiBase,
    authData?.organizations,
    messageId,
    selectedPractice?.id,
    setSelectedPractice,
    navigate,
    tokens?.id_token,
    user?.id,
  ]);

  useEffect(() => {
    if (!composeOpen || !tokens?.id_token || !selectedPractice?.id) {
      setRecipientOptions([]);
      setRecipientLoading(false);
      return;
    }

    const controller = new AbortController();
    const loadRecipients = async () => {
      setRecipientLoading(true);
      try {
        const params = new URLSearchParams({
          skip: '0',
          limit: '25',
        });
        if (recipientSearchInput.trim()) {
          params.set('search_text', recipientSearchInput.trim());
        }
        const response = await fetch(
          `${apiBase}/organizations/${selectedPractice.id}/users?${params.toString()}`,
          {
            headers: { Authorization: `Bearer ${tokens.id_token}` },
            signal: controller.signal,
          }
        );
        if (!response.ok) {
          throw new Error('Failed to load recipients');
        }
        const data = await response.json();
        if (!controller.signal.aborted) {
          setRecipientOptions(Array.isArray(data.items) ? data.items : []);
        }
      } catch (err) {
        if (!controller.signal.aborted) {
          setRecipientOptions([]);
        }
      } finally {
        if (!controller.signal.aborted) {
          setRecipientLoading(false);
        }
      }
    };

    loadRecipients();
    return () => controller.abort();
  }, [apiBase, composeOpen, recipientSearchInput, selectedPractice?.id, tokens?.id_token]);

  const runMessageAction = async (targetMessageId, action) => {
    if (!tokens?.id_token) return null;

    setActionTargetId(String(targetMessageId));
    setActionError('');
    try {
      const response = await fetch(`${apiBase}/messages/${targetMessageId}/${action}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${tokens.id_token}` },
      });
      if (!response.ok) {
        throw new Error(action === 'archive' ? 'Failed to archive message' : 'Failed to update message');
      }

      const updated = await response.json();
      setDetail((current) => (
        current && String(current.id) === String(updated.id) ? updated : current
      ));
      setListRefreshNonce((current) => current + 1);
      dispatchMessagesChanged();
      return updated;
    } catch (err) {
      setActionError(err.message || 'Failed to update message');
      return null;
    } finally {
      setActionTargetId(null);
    }
  };

  const resetComposeDialog = () => {
    setRecipientSearchInput('');
    setSelectedRecipient(null);
    setComposeSubject('');
    setComposeBody('');
    setComposeAttachments([]);
    setComposeError('');
    setComposeSubmitting(false);
  };

  const handleOpenComposeDialog = () => {
    resetComposeDialog();
    setComposeOpen(true);
  };

  const handleCloseComposeDialog = () => {
    if (composeSubmitting) return;
    setComposeOpen(false);
    resetComposeDialog();
  };

  const handleComposeAttachmentSelection = (event) => {
    const newFiles = Array.from(event.target.files || []);
    if (newFiles.length > 0) {
      setComposeAttachments((current) => [...current, ...newFiles]);
    }
    event.target.value = '';
  };

  const handleRemoveComposeAttachment = (indexToRemove) => {
    setComposeAttachments((current) => current.filter((_, index) => index !== indexToRemove));
  };

  const handleDownloadAttachment = async (attachment) => {
    if (!tokens?.id_token) return;
    try {
      const response = await fetch(`${apiBase}${attachment.download_url}`, {
        headers: { Authorization: `Bearer ${tokens.id_token}` },
      });
      if (!response.ok) {
        throw new Error('Failed to download attachment');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.attachment_name || 'attachment';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setActionError(err.message || 'Failed to download attachment');
    }
  };

  const handleOpenAttachmentInTab = (attachment) => {
    if (!tokens?.id_token || !attachment?.download_url) return;
    const openedTab = window.open('', '_blank');

    fetch(`${apiBase}${attachment.download_url}?disposition=inline`, {
      headers: { Authorization: `Bearer ${tokens.id_token}` },
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error('Failed to open attachment');
        }
        return response.blob();
      })
      .then((blob) => {
        const blobUrl = window.URL.createObjectURL(blob);
        if (openedTab) {
          openedTab.location = blobUrl;
        } else {
          window.open(blobUrl, '_blank');
        }
        window.setTimeout(() => window.URL.revokeObjectURL(blobUrl), 60_000);
      })
      .catch((err) => {
        if (openedTab) {
          openedTab.close();
        }
        setActionError(err.message || 'Failed to open attachment');
      });
  };

  const handleComposeSubmit = async () => {
    if (!tokens?.id_token || !selectedPractice?.id) return;
    if (!selectedRecipient?.id) {
      setComposeError('Recipient is required');
      return;
    }
    if (!composeSubject.trim()) {
      setComposeError('Subject is required');
      return;
    }
    if (!composeBody.trim()) {
      setComposeError('Message is required');
      return;
    }

    setComposeSubmitting(true);
    setComposeError('');
    try {
      const formData = new FormData();
      formData.append('organization_id', selectedPractice.id);
      formData.append('to_user_id', selectedRecipient.id);
      formData.append('subject', composeSubject.trim());
      formData.append('message', composeBody.trim());
      formData.append('type', 'person-to-person');
      composeAttachments.forEach((file) => {
        formData.append('attachments', file);
      });

      const response = await fetch(`${apiBase}/messages`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${tokens.id_token}` },
        body: formData,
      });
      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.detail || 'Failed to send message');
      }

      await response.json();
      setComposeOpen(false);
      resetComposeDialog();
      setListRefreshNonce((current) => current + 1);
      dispatchMessagesChanged();
    } catch (err) {
      setComposeError(err.message || 'Failed to send message');
    } finally {
      setComposeSubmitting(false);
    }
  };

  const handleDateSortToggle = () => {
    setDateSortDirection((current) => (current === 'desc' ? 'asc' : 'desc'));
    setPage(0);
  };

  const renderListStateRow = () => {
    if (!selectedPractice?.id) {
      return (
        <TableRow>
          <TableCell colSpan={6}>
            <Alert severity="info">Select a practice or lab to view messages.</Alert>
          </TableCell>
        </TableRow>
      );
    }

    if (loading) {
      return (
        <TableRow>
          <TableCell colSpan={6}>
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress size={24} />
            </Box>
          </TableCell>
        </TableRow>
      );
    }

    if (error) {
      return (
        <TableRow>
          <TableCell colSpan={6}>
            <Alert severity="error">{error}</Alert>
          </TableCell>
        </TableRow>
      );
    }

    if (messages.length === 0) {
      return (
        <TableRow>
          <TableCell colSpan={6}>
            <Typography color="text.secondary">No messages found for the current filters.</Typography>
          </TableCell>
        </TableRow>
      );
    }

    return messages.map((message) => {
      const isBusy = actionTargetId === String(message.id);
      const isRead = isMessageRead(message);
      return (
        <TableRow
          key={message.id}
          hover
          onClick={() => navigate(`/messages/${message.id}`)}
          sx={{
            cursor: 'pointer',
            backgroundColor: isRead ? 'transparent' : 'rgba(25, 118, 210, 0.08)',
            '&:hover': {
              backgroundColor: isRead ? 'action.hover' : 'rgba(25, 118, 210, 0.14)',
            },
          }}
        >
          <TableCell align="center" sx={{ width: 24, px: 0.25 }}>
            <Tooltip title={message.type || 'message'}>
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                {getMessageIcon(message)}
              </Box>
            </Tooltip>
          </TableCell>
          <TableCell align="center" sx={{ width: 24, px: 0.25 }}>
            {message.attachments?.length > 0 ? (
              <Tooltip title={`${message.attachments.length} attachment${message.attachments.length === 1 ? '' : 's'}`}>
                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                  <AttachFileIcon fontSize="small" />
                </Box>
              </Tooltip>
            ) : null}
          </TableCell>
          <TableCell sx={{ fontWeight: isRead ? 400 : 700, minWidth: 0 }}>
            <Tooltip title={message.subject}>
              <Typography
                variant="body2"
                sx={{
                  display: 'block',
                  width: '100%',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'clip',
                }}
              >
                {truncateMiddle(message.subject)}
              </Typography>
            </Tooltip>
          </TableCell>
          <TableCell
            align="left"
            sx={{ width: 96, whiteSpace: 'nowrap', fontWeight: isRead ? 400 : 700, pl: 0.5, pr: 0.5 }}
          >
            <Typography variant="body2" noWrap sx={{ fontWeight: 'inherit' }}>
              {getMessageFromLabel(message)}
            </Typography>
          </TableCell>
          <TableCell
            align="left"
            sx={{ width: 84, whiteSpace: 'nowrap', fontWeight: isRead ? 400 : 700, pl: 0.5, pr: 0.5 }}
          >
            <Typography variant="body2" noWrap sx={{ fontWeight: 'inherit' }}>
              {formatMessageTableDate(message.created_at)}
            </Typography>
          </TableCell>
          <TableCell align="left" sx={{ width: 76, whiteSpace: 'nowrap', pl: 0.5 }} onClick={(event) => event.stopPropagation()}>
            <Stack direction="row" spacing={0.5} justifyContent="flex-start">
              <Tooltip title={isRead ? 'Mark unread' : 'Mark read'}>
                <span>
                  <IconButton
                    size="small"
                    disabled={isBusy}
                    onClick={() => runMessageAction(message.id, 'toggle-read')}
                  >
                    {isRead ? <MarkEmailUnreadIcon fontSize="small" /> : <MarkEmailReadIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={message.is_archived ? 'Active' : 'Archive'}>
                <span>
                  <IconButton
                    size="small"
                    disabled={isBusy}
                    onClick={() => runMessageAction(message.id, 'archive')}
                  >
                    {message.is_archived ? <UnarchiveIcon fontSize="small" /> : <ArchiveIcon fontSize="small" />}
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </TableCell>
        </TableRow>
      );
    });
  };

  if (messageId) {
    return (
      <Container disableGutters sx={{ mt: 2 }}>
        <Stack spacing={2}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
            <Button
              variant="text"
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/messages')}
            >
              Back to Messages
            </Button>
            {detail && (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={isMessageRead(detail) ? <MarkEmailUnreadIcon /> : <MarkEmailReadIcon />}
                  disabled={actionTargetId === String(detail.id)}
                  onClick={async () => {
                    const updated = await runMessageAction(detail.id, 'toggle-read');
                    if (updated) {
                      setDetail(updated);
                    }
                  }}
                >
                  {isMessageRead(detail) ? 'Mark unread' : 'Mark read'}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="inherit"
                  startIcon={detail.is_archived ? <UnarchiveIcon /> : <ArchiveIcon />}
                  disabled={actionTargetId === String(detail.id)}
                  onClick={async () => {
                    const updated = await runMessageAction(detail.id, 'archive');
                    if (updated) {
                      setDetail(updated);
                    }
                  }}
                >
                  {detail.is_archived ? 'Active' : 'Archive'}
                </Button>
              </Stack>
            )}
          </Box>

          {actionError && <Alert severity="error">{actionError}</Alert>}
          {detailError && <Alert severity="error">{detailError}</Alert>}

          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress />
            </Box>
          ) : detail ? (
            <Stack spacing={2}>
              <Card variant="outlined">
                <CardContent>
                  <Stack spacing={2}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, alignItems: 'flex-start' }}>
                      <Box />
                      <MessageStatusChip message={detail} />
                    </Box>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} useFlexGap flexWrap="wrap">
                      <Box>
                        <Typography variant="caption" color="text.secondary">From</Typography>
                        <Typography variant="body2">{getMessageFromLabel(detail)}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Date</Typography>
                        <Typography variant="body2">{formatMessageDate(detail.created_at)}</Typography>
                      </Box>
                      <Box>
                        <Typography variant="caption" color="text.secondary">Type</Typography>
                        <Tooltip title={detail.type || 'message'}>
                          <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 20 }}>
                            {getMessageIcon(detail)}
                          </Box>
                        </Tooltip>
                      </Box>
                    </Stack>
                  </Stack>
                </CardContent>
              </Card>

              <Card variant="outlined">
                <CardContent>
                  {renderMarkdownBlocks(`# ${detail.subject}\n\n${detail.message || ''}`)}
                </CardContent>
              </Card>

              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1.5 }}>
                    Attachments
                  </Typography>
                  {detail.attachments?.length > 0 ? (
                    <Stack spacing={1}>
                      {detail.attachments.map((attachment) => (
                        <Stack key={attachment.id} direction="row" spacing={1} alignItems="center">
                          <Tooltip title="Download">
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<AttachFileIcon />}
                              onClick={() => handleDownloadAttachment(attachment)}
                            >
                              {attachment.attachment_name}
                            </Button>
                          </Tooltip>
                          <Tooltip title="Open in tab">
                            <IconButton
                              size="small"
                              onClick={() => handleOpenAttachmentInTab(attachment)}
                            >
                              <OpenInNewIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      ))}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No attachments.
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Stack>
          ) : null}
        </Stack>
      </Container>
    );
  }

  return (
    <Container disableGutters sx={{ mt: 2 }}>
      <Stack spacing={2}>
        {actionError && <Alert severity="error">{actionError}</Alert>}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            sx={{ minWidth: 220 }}
            onClick={handleOpenComposeDialog}
            disabled={!selectedPractice?.id}
          >
            New Message
          </Button>
        </Box>

        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
          <TextField
            label="Search Messages"
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            size="small"
            fullWidth
            disabled={!selectedPractice?.id}
          />
          <TextField
            select
            label="Status"
            value={statusFilter}
            onChange={(event) => {
              setStatusFilter(event.target.value);
              setPage(0);
            }}
            size="small"
            sx={{ minWidth: 180 }}
            disabled={!selectedPractice?.id}
          >
            <MenuItem value="all">All</MenuItem>
            <MenuItem value="unread">Unread</MenuItem>
            <MenuItem value="read">Read</MenuItem>
          </TextField>
          <TextField
            select
            label="Archived"
            value={archivedFilter}
            onChange={(event) => {
              setArchivedFilter(event.target.value);
              setPage(0);
            }}
            size="small"
            sx={{ minWidth: 180 }}
            disabled={!selectedPractice?.id}
          >
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="archived">Archived</MenuItem>
            <MenuItem value="all">All</MenuItem>
          </TextField>
        </Stack>

        <TableContainer component={Paper} variant="outlined">
          <Table size="small" sx={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell align="center" sx={{ width: 24, px: 0.25 }} />
                <TableCell align="center" sx={{ width: 24, px: 0.25 }} />
                <TableCell>Subject</TableCell>
                <TableCell align="left" sx={{ width: 96, whiteSpace: 'nowrap', pl: 0.5, pr: 0.5 }}>From</TableCell>
                <TableCell align="left" sx={{ width: 84, whiteSpace: 'nowrap', pl: 0.5, pr: 0.5 }}>
                  <TableSortLabel
                    active
                    direction={dateSortDirection}
                    onClick={handleDateSortToggle}
                  >
                    Date
                  </TableSortLabel>
                </TableCell>
                <TableCell align="left" sx={{ width: 76, whiteSpace: 'nowrap', pl: 0.5 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {renderListStateRow()}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={selectedPractice?.id ? total : 0}
          page={page}
          onPageChange={(_, nextPage) => setPage(nextPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
        />

        <Dialog open={composeOpen} onClose={handleCloseComposeDialog} fullWidth maxWidth="sm">
          <DialogTitle>New Message</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ mt: 1 }}>
              {composeError && <Alert severity="error">{composeError}</Alert>}
              <Autocomplete
                options={recipientOptions}
                value={selectedRecipient}
                onChange={(_, value) => setSelectedRecipient(value)}
                inputValue={recipientSearchInput}
                onInputChange={(_, value) => setRecipientSearchInput(value)}
                loading={recipientLoading}
                isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                getOptionLabel={(option) => {
                  const name = option.name || 'Unknown user';
                  const email = option.email || '';
                  return email ? `${name} (${email})` : name;
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Recipient"
                    placeholder="Search users in this organization"
                    helperText="You can send a message to anyone in this organization, including yourself."
                  />
                )}
              />
              <TextField
                label="Subject"
                value={composeSubject}
                onChange={(event) => setComposeSubject(event.target.value)}
                fullWidth
              />
              <TextField
                label="Message"
                value={composeBody}
                onChange={(event) => setComposeBody(event.target.value)}
                multiline
                minRows={5}
                fullWidth
              />
              <Stack spacing={1}>
                <Box>
                  <Button
                    variant="outlined"
                    component="label"
                    startIcon={<AttachFileIcon />}
                  >
                    Add Attachments
                    <input
                      hidden
                      type="file"
                      multiple
                      onChange={handleComposeAttachmentSelection}
                    />
                  </Button>
                </Box>
                {composeAttachments.length > 0 && (
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {composeAttachments.map((file, index) => (
                      <Chip
                        key={`${file.name}-${file.size}-${index}`}
                        label={file.name}
                        onDelete={() => handleRemoveComposeAttachment(index)}
                        variant="outlined"
                      />
                    ))}
                  </Stack>
                )}
              </Stack>
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseComposeDialog} disabled={composeSubmitting}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleComposeSubmit}
              disabled={composeSubmitting}
            >
              {composeSubmitting ? 'Sending...' : 'Send Message'}
            </Button>
          </DialogActions>
        </Dialog>
      </Stack>
    </Container>
  );
}
